/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';

import { breakpoints } from '@coursera/cds-common';
import { Button, Divider, InlineNotification } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import CourseAccordionRow from 'bundles/account-settings/components/sections/email-preferences/CourseAccordionRow';
import { Sections, UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type {
  CourseEmailPreference,
  CourseEmailType,
  UpdateStatus,
} from 'bundles/account-settings/types/EmailPreferences';
import { COURSE_EMAIL_TYPES } from 'bundles/account-settings/types/EmailPreferences';
import { getStatusMessages } from 'bundles/user-account/utils/errorMessageUtils';

import _t from 'i18n!nls/account-settings';

type Props = {
  coursePreferences: CourseEmailPreference[];
  updateStatus: UpdateStatus;
  disabled: boolean;
};

const styles = {
  list: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  buttonRow: css`
    margin-block-start: var(--cds-spacing-200);
  `,
  saveButton: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: unset;
    }
  `,
  inlineNotification: css`
    margin-block-start: var(--cds-spacing-200);
  `,
};

const CourseEmailPreferencesList = ({ coursePreferences, updateStatus, disabled }: Props) => {
  const client = useApolloClient();
  const track = useTracker();
  const [preferences, setPreferences] = useState<CourseEmailPreference[]>(coursePreferences);
  const [toggledPreferencesForTracking, setToggledPreferencesForTracking] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    setPreferences(coursePreferences);
  }, [coursePreferences]);

  const handleCourseToggle = (courseId: string, type: CourseEmailType, checked: boolean) => {
    const updatedPreferences = preferences.map((pref) => {
      if (pref.id !== courseId) return pref;
      return { ...pref, value: { ...pref.value, [type]: checked } };
    });
    setPreferences(updatedPreferences);
    setToggledPreferencesForTracking((prev) => ({ ...prev, [`${courseId}_${type}`]: true }));
    SettingsActionCreators.updatePendingChangesStatus(Sections.COURSE_EMAIL_PREFERENCES, true);
  };

  const handleSave = () => {
    if (preferences.length === 0) return;
    SettingsActionCreators.updateEmailPreferences(Sections.COURSE_EMAIL_PREFERENCES, preferences, client);
    // Same as old settings page: fire tracking events after save, not on toggle
    preferences.forEach((preference) => {
      COURSE_EMAIL_TYPES.forEach((type) => {
        if (toggledPreferencesForTracking[`${preference.id}_${type}`]) {
          track('click_checkbox', {
            checkbox: {
              name: 'enrolled_courses_communication_email_preference',
              label: type,
              checked: preference.value[type],
            },
            product: {
              type: 'course',
              id: preference.id,
              name: preference.name,
            },
          });
        }
      });
    });
    setToggledPreferencesForTracking({});
  };

  const isUpdating = updateStatus.state === UpdateStates.UPDATING;

  return (
    <div>
      <div css={styles.list}>
        {preferences.map((course, index) => (
          <React.Fragment key={course.id}>
            {index > 0 && <Divider color="light" orientation="horizontal" />}
            <CourseAccordionRow
              course={course}
              disabled={disabled}
              onToggle={(type, checked) => handleCourseToggle(course.id, type, checked)}
            />
          </React.Fragment>
        ))}
      </div>

      <div css={styles.buttonRow}>
        <Button
          variant="secondary"
          size="small"
          loading={isUpdating}
          disabled={disabled}
          onClick={handleSave}
          css={styles.saveButton}
        >
          {_t('Save Changes')}
        </Button>
      </div>

      <div role="status" aria-atomic="true">
        {updateStatus.state === UpdateStates.SUCCEEDED && (
          <InlineNotification css={styles.inlineNotification} severity="success">
            {getStatusMessages.UPDATE_SUCCEEDED}
          </InlineNotification>
        )}
      </div>
      <div role="alert" aria-atomic="true">
        {updateStatus.state === UpdateStates.FAILED && (
          <InlineNotification css={styles.inlineNotification} severity="error">
            {getStatusMessages.UPDATE_FAILED}
          </InlineNotification>
        )}
      </div>
    </div>
  );
};

export default CourseEmailPreferencesList;
